import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ApiResponse } from '@/api/http/types'
import type { Campaign, CampaignInstrument } from '../types'

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export function useCampaigns(options?: { refetchIntervalMs?: number }) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.campaigns.list(tenantKey(tenantId)),
    queryFn: async () => {
      const { data: envelope } =
        await apiClient.get<ApiResponse<Campaign[]>>('/api/v1/campaigns')
      return { items: envelope.data, meta: envelope.meta }
    },
    enabled: isAuthenticated,
    refetchInterval: options?.refetchIntervalMs ?? false,
  })
}

export function useCampaign(
  campaignId: string,
  options?: { refetchIntervalMs?: number },
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.campaigns.detail(tenantKey(tenantId), campaignId),
    queryFn: async () => {
      const { data: envelope } = await apiClient.get<ApiResponse<Campaign>>(
        `/api/v1/campaigns/${campaignId}`,
      )
      return envelope.data
    },
    enabled:
      isAuthenticated && !!campaignId && campaignId !== 'new',
    refetchInterval: options?.refetchIntervalMs ?? false,
  })
}

export function useCampaignInstruments(
  campaignId: string,
  options?: { refetchIntervalMs?: number; enabled?: boolean },
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.campaigns.instruments(
      tenantKey(tenantId),
      campaignId,
    ),
    queryFn: async () => {
      const { data: envelope } = await apiClient.get<
        ApiResponse<CampaignInstrument[]>
      >(`/api/v1/campaigns/${campaignId}/instruments`)
      return envelope.data
    },
    enabled:
      (options?.enabled ?? true) &&
      isAuthenticated &&
      !!campaignId &&
      campaignId !== 'new',
    refetchInterval: options?.refetchIntervalMs ?? false,
  })
}
